import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Row
} from 'antd'

import {
  Gender,
  HivTestResult,
  TriState,
  HivAnamnesisFormSection
} from 'src/api/requests'
import { Text } from 'src/components'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'

import { useHivAnamnesisForm } from '../../context/HivAnamnesisFormContext'
import { EnumRadio } from '../fields/EnumRadio'
import { TriStateItem } from '../fields/TriStateItem'
import { HivFormItem } from '../HivFormItem'
import { useIsReadOnlyForm } from '../ReadOnly'
import { SystemRefreshButton } from '../SystemRefreshButton'
import { TriStateRadio } from '../TriStateRadio'

import { useIsChild } from './TransmissionCard'

/** Children rows printed on the form (48, 49, 50); the list itself is unlimited. */
const PRINTED_CHILDREN = 3

const WOMAN_QUESTIONS = [
  ['47.1', 'gaveBirthBeforeDiagnosis'],
  ['47.2', 'currentlyPregnant'],
  ['47.3', 'pregnantAfterDiagnosis']
] as const

/**
 * 47.1–47.3, optional (also their date) since 2026-10-15. Also used by the yearly ECDC update for 47.3, under
 * another path.
 */
export const WomanQuestion = ({
  field,
  number,
  path: basePath = ['form', 'family', field]
}: {
  field: (typeof WOMAN_QUESTIONS)[number][1]
  number: string
  path?: (string | number)[]
}) => {
  const { t } = useLocales()
  const form = Form.useFormInstance()
  const path = (part: string) => [...basePath, part]
  const answer = Form.useWatch(path('answer'), form)

  return (
    <Row gutter={[24, 24]}>
      <TriStateItem
        name={path('answer')}
        number={number}
        label={t(`hiv_anamnesis.family.q.${field}`)}
      />

      {answer === TriState.YES && (
        <Col xs={24} lg={6}>
          <HivFormItem
            name={path('date')}
            label={t('hiv_anamnesis.family.date')}
          >
            <DatePicker
              className="full-width"
              format={dayMonthYearFormatWithDot}
            />
          </HivFormItem>
        </Col>
      )}
    </Row>
  )
}

/**
 * E. Family data (fields 44–50). 47.x is for women (optional for "Altul", hidden for men); the children
 * (48–50) are for women aged 15 and over, a list without the paper form's limit of three, kept in the form only.
 */
export const FamilyCard = () => {
  const { t } = useLocales()
  const form = Form.useFormInstance()
  const gender = Form.useWatch(['form', 'patient', 'gender'], form)
  // 48–50 are for women aged 15 and over (on the collection date); hidden for men, "Altul" and children.
  const isChild = useIsChild()
  const showChildren = gender === Gender.FEMALE && !isChild
  const children = Form.useWatch(['form', 'family', 'children'], form)
  const { userHasAccessToPersonalData } = useHivAnamnesisForm()
  const isReadOnly = useIsReadOnlyForm()

  const path = (...parts: string[]) => ['form', 'family', ...parts]

  return (
    <Card
      title={t('hiv_anamnesis.family.title')}
      extra={
        <SystemRefreshButton sections={[HivAnamnesisFormSection.FAMILY]} />
      }
    >
      <Row gutter={[24, 24]}>
        <TriStateItem
          name={path('spouseInfected')}
          number="44"
          label={t('hiv_anamnesis.family.q.spouseInfected')}
          span={8}
        />
        <TriStateItem
          name={path('advisedToDiscloseStatus')}
          number="45"
          label={t('hiv_anamnesis.family.q.advisedToDiscloseStatus')}
          span={8}
        />
        <TriStateItem
          name={path('partnerTested')}
          number="46"
          label={t('hiv_anamnesis.family.q.partnerTested')}
          span={8}
        />
      </Row>

      {gender !== Gender.MALE && (
        <>
          <Divider />
          <Text size="xl">{`47. ${t('hiv_anamnesis.family.woman_title')}`}</Text>
          <Flex vertical gap={24} style={{ marginTop: 16 }}>
            {WOMAN_QUESTIONS.map(([number, field]) => (
              <WomanQuestion key={field} field={field} number={number} />
            ))}
          </Flex>
        </>
      )}

      {showChildren && (
        <>
          <Divider />
          <Text size="xl">{`48–50. ${t('hiv_anamnesis.family.children_title')}`}</Text>

          <Form.List name={path('children')}>
            {(fields, { add, remove }) => (
              <Flex vertical gap={24} style={{ marginTop: 16 }}>
                {fields.map(({ key, name }) => {
                  const tested = children?.[name]?.testedForHiv === TriState.YES
                  // The names of saved children are personal data: hidden and kept as they are for a user without
                  // access. A child the user adds is entered normally.
                  const isSaved =
                    !!children?.[name]?.id || !!children?.[name]?.personId
                  const nameHidden = !userHasAccessToPersonalData && isSaved
                  // The form has three children: 48.x, 49.x, 50.x. Further ones have no number.
                  const rowNumber =
                    name < PRINTED_CHILDREN ? String(48 + name) : undefined
                  const sub = (part: number) =>
                    rowNumber ? `${rowNumber}.${part}` : undefined

                  return (
                    <Row key={key} gutter={[24, 24]} align="middle">
                      <Col xs={24} lg={6}>
                        <HivFormItem
                          name={[name, 'name']}
                          number={rowNumber}
                          label={t('hiv_anamnesis.family.child_name')}
                        >
                          <Input
                            maxLength={250}
                            disabled={nameHidden || undefined}
                            placeholder={
                              nameHidden
                                ? t('hiv_anamnesis.family.child_name_hidden')
                                : undefined
                            }
                          />
                        </HivFormItem>
                      </Col>

                      <Col xs={24} lg={4}>
                        <HivFormItem
                          name={[name, 'birthDate']}
                          number={sub(1)}
                          label={t('hiv_anamnesis.family.child_birth_date')}
                        >
                          <DatePicker
                            className="full-width"
                            format={dayMonthYearFormatWithDot}
                            disabledDate={disabledFutureDate}
                          />
                        </HivFormItem>
                      </Col>

                      <Col xs={24} lg={5}>
                        <HivFormItem
                          name={[name, 'testedForHiv']}
                          number={sub(2)}
                          label={t('hiv_anamnesis.family.child_tested')}
                        >
                          <TriStateRadio
                            onChange={(e) => {
                              if (e.target.value !== TriState.YES) {
                                form.setFieldValue(
                                  [...path('children'), name, 'result'],
                                  undefined
                                )
                              }
                            }}
                          />
                        </HivFormItem>
                      </Col>

                      <Col xs={20} lg={8}>
                        <HivFormItem
                          name={[name, 'result']}
                          number={sub(3)}
                          label={t('hiv_anamnesis.family.child_result')}
                        >
                          <EnumRadio
                            values={Object.values(HivTestResult)}
                            labelPrefix="hiv_anamnesis.test_result"
                            disabled={!tested || undefined}
                          />
                        </HivFormItem>
                      </Col>

                      {!isReadOnly && (
                        <Col xs={4} lg={1}>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                          />
                        </Col>
                      )}
                    </Row>
                  )
                })}

                {!isReadOnly && (
                  <Button icon={<PlusOutlined />} onClick={() => add({})}>
                    {t('hiv_anamnesis.family.add_child')}
                  </Button>
                )}
              </Flex>
            )}
          </Form.List>
        </>
      )}
    </Card>
  )
}
